import { useAuth0 } from '@auth0/auth0-react';
import { ConnectStediStep } from '@features/billing-readiness/components/wizard/ConnectStediStep';
import { useCan } from '@features/user';
import { Alert, Box, Typography } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import { cardSx } from './tokens';

interface IntakeStediConnectionProps {
  organizationId?: string;
}

/** Credentials belong to the Stedi connection API, never to intake answers or autosave. */
export function IntakeStediConnection({ organizationId }: IntakeStediConnectionProps) {
  const enabled = useFeatureGate(FeatureGates.PAYER_CONNECTIONS);
  const canManageBilling = useCan('manage_billing');
  const { user, isAuthenticated } = useAuth0();
  const { intakeHub } = useEventTracking();
  const isAdmin = organizationId !== undefined;
  // Organization membership roles from user-info are not Auth0 staff roles.
  // Match the admin routes and the Stedi mutation endpoint's staff guard.
  const staffRoles: unknown = user?.['https://api.smartscribe.health/roles'];
  const isStaffAdmin = isAuthenticated && Array.isArray(staffRoles) && staffRoles.includes('Admin');
  const canConnect = isAdmin ? isStaffAdmin : canManageBilling;

  if (!enabled) return null;

  return (
    <Box component="section" aria-label="Stedi connection" sx={{ ...cardSx, gridColumn: '1 / -1' }}>
      <Typography component="h2" variant="h6" sx={{ mb: 1 }}>
        Connect Stedi
      </Typography>
      {isAdmin || canConnect ? (
        <ConnectStediStep
          organizationId={organizationId}
          readOnly={!canConnect}
          onConnected={() => undefined}
          onAction={
            isAdmin ? undefined : action => intakeHub.stediConnectionActionClicked({ action })
          }
        />
      ) : (
        <Alert severity="info">
          Ask your practice administrator or billing administrator to connect your Stedi account.
        </Alert>
      )}
    </Box>
  );
}
